/**
 * Utilities for handling composite IDs used in search results.
 *
 * Search results use composite IDs in the format "threadId-sessionId" to uniquely
 * identify different sessions within the same thread. These utilities help extract
 * the original threadId when needed (e.g., for generating shareable links).
 */

/**
 * Checks if an ID is a composite ID (contains a hyphen).
 * CUIDs don't contain hyphens, so this is a safe check.
 */
export function isCompositeId(id: string): boolean {
  return id.includes('-')
}

/**
 * Extracts the original threadId from a composite ID.
 * Composite IDs are in the format "threadId-sessionId".
 *
 * @param id - Either a composite ID (threadId-sessionId) or a regular threadId
 * @returns The original threadId
 */
export function extractThreadId(id: string): string {
  if (!isCompositeId(id)) {
    return id
  }
  return id.split('-')[0]
}

/**
 * Extracts the sessionId from a composite ID.
 * Returns undefined if the ID is not a composite ID.
 *
 * @param id - Either a composite ID (threadId-sessionId) or a regular threadId
 * @returns The sessionId if composite, undefined otherwise
 */
export function extractSessionId(id: string): string | undefined {
  if (!isCompositeId(id)) {
    return undefined
  }
  return id.split('-')[1]
}

/**
 * Creates a composite ID from a threadId and sessionId.
 *
 * @param threadId - The thread ID
 * @param sessionId - The session ID
 * @returns A composite ID in the format "threadId-sessionId"
 */
export function createCompositeId(threadId: string, sessionId: string): string {
  return `${threadId}-${sessionId}`
}
